import type { CTRL_SCHEMA } from "@repo/sdk/types";
import { useHttp, type HttpConfiguration } from "@repo/utils/hooks";
import { useCtrlClient } from "../contexts/CtrlApiProvider";

export type PermissionsSummary = CTRL_SCHEMA["OpenapigenPermissionsSummary"];
export type PermittedResource = CTRL_SCHEMA["OpenapigenPermittedResource"];

/**
 * Resource kinds tracked by the permissions summary. Derived directly
 * from the auto-generated SDK schema so this stays in sync with the
 * backend automatically (no local drift).
 */
export type ResourceKind = Exclude<keyof PermissionsSummary, "is_global_admin">;

const SWR_KEY = "permissions:me";

/**
 * Server-driven permission state for the authenticated viewer.
 *
 * Backed by `GET /api/v1/permissions/me` via the canonical
 * `useCtrlClient` + SWR pattern. The hook is the single source of truth
 * for authorization decisions in the portal — never trust JWT roles or
 * local heuristics.
 *
 * `canManage` / `canOperate` / `canView` accept a type-safe
 * {@link ResourceKind} and short-circuit to `true` for global admins.
 *
 * Use `refresh()` to invalidate the cache after an admin mutation that
 * may have changed the viewer's own access (see `useApiMutation`'s
 * `invalidatePermissions` option in the portal).
 */
export function usePermissions(config?: HttpConfiguration) {
  const client = useCtrlClient();

  const swr = useHttp<PermissionsSummary>(
    client.token ? SWR_KEY : null,
    async () => client.getMyPermissions(),
    { revalidateOnFocus: false, dedupingInterval: 30_000, ...config },
  );

  const { data, error, isLoading, mutate } = swr;
  const isGlobalAdmin = data?.is_global_admin ?? false;

  const canManage = (resourceType: ResourceKind, resourceId: string): boolean => {
    if (isGlobalAdmin) return true;
    const resources = data?.[resourceType];
    if (!Array.isArray(resources)) return false;
    return resources.some(
      (r) => r.id === resourceId && r.relations?.includes("can_manage"),
    );
  };

  const canOperate = (resourceType: ResourceKind, resourceId: string): boolean => {
    if (isGlobalAdmin) return true;
    const resources = data?.[resourceType];
    if (!Array.isArray(resources)) return false;
    return resources.some(
      (r) =>
        r.id === resourceId &&
        (r.relations?.includes("can_operate") ||
          r.relations?.includes("can_manage")),
    );
  };

  const canView = (resourceType: ResourceKind, resourceId: string): boolean => {
    if (isGlobalAdmin) return true;
    const resources = data?.[resourceType];
    if (!Array.isArray(resources)) return false;
    return resources.some((r) => r.id === resourceId);
  };

  return {
    permissions: data,
    isLoading,
    error,
    canManage,
    canOperate,
    canView,
    isGlobalAdmin,
    refresh: mutate,
  };
}
